"use client";

import { c as _c } from "react/compiler-runtime";
import { EnhancedJsonViewWithCopy, hasNestedContent } from "@/components/ui/enhanced-json-view";
import type { FundsMovementMetadata } from "@/hooks/useActionTriggerDefinitions";
import type { WorkflowNodeExecutionState } from "@/stores/workflowRunsStore";
import { resolvePayloadTemplates } from "@/lib/workflow/resolvePayloadTemplates";
import { type Dispatch, type SetStateAction, useMemo } from "react";
import { FundsMovementBanner, type SchemaNode } from "./FundsMovementBanner";
import { CodeTransformPayload } from "./RunDetailContent";
import { ErrorDisplay, ResultDisplay } from "./ResultDisplay";
import { RunTimelineIterationRow } from "./RunTimeline";
interface ActionDefinition {
  type: string;
  fundsMovement?: FundsMovementMetadata;
  resultSchema?: SchemaNode;
  payloadSchema?: SchemaNode;
}
interface IterationStepRowProps {
  childNodeId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  childState: any;
  childDef: {
    type: string;
    payload?: Record<string, unknown>;
  } | undefined;
  childLogoUrl?: string;
  childConnector?: {
    type: string;
    logoUrl?: string;
  };
  iterStepKey: string;
  isExpanded: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  onToggle: () => void;
  expandedPayloads: Set<string>;
  setExpandedPayloads: Dispatch<SetStateAction<Set<string>>>;
  executionState: Record<string, WorkflowNodeExecutionState>;
  getNodeDisplayName: (nodeType: string) => string;
  walletAddress?: string;
  /** Whether the parent run is a simulation. */
  isSimulation?: boolean;
  /** Action definition from the registry (has fundsMovement, schemas) */
  actionDefinition?: ActionDefinition;
}
export function IterationStepRow(t0) {
  const $ = _c(54);
  const {
    childNodeId,
    childState,
    childDef,
    childLogoUrl,
    childConnector,
    iterStepKey,
    isExpanded,
    isFirst: t1,
    isLast: t2,
    onToggle,
    expandedPayloads,
    setExpandedPayloads,
    executionState,
    getNodeDisplayName,
    walletAddress,
    isSimulation,
    actionDefinition
  } = t0;
  const isFirst = t1 === undefined ? false : t1;
  const isLast = t2 === undefined ? false : t2;
  const childDuration = childState?.startedAt && childState?.finishedAt ? new Date(childState.finishedAt).getTime() - new Date(childState.startedAt).getTime() : undefined;
  const loopVars = childState?.context?.loopVars as Record<string, unknown> | undefined;
  childDef?.payload;
  let t3;
  const t4 = childDef?.payload;
  let t5;
  if ($[0] !== loopVars || $[1] !== t4) {
    t5 = resolvePayloadTemplates(t4, loopVars);
    $[0] = loopVars;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  t3 = t5;
  const resolvedPayload = t3;
  const t6 = childDef?.type || childNodeId;
  let t7;
  if ($[3] !== getNodeDisplayName || $[4] !== t6) {
    t7 = getNodeDisplayName(t6);
    $[3] = getNodeDisplayName;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = childDef?.type || childNodeId;
  const t9 = childState?.status || "pending";
  const t10 = childState?.error;
  let t11;
  if ($[6] !== actionDefinition || $[7] !== childState?.result || $[8] !== childState?.status || $[9] !== isSimulation || $[10] !== resolvedPayload || $[11] !== walletAddress) {
    t11 = actionDefinition?.fundsMovement && <div className="py-2"><FundsMovementBanner movement={actionDefinition.fundsMovement} payload={resolvedPayload} result={childState?.result} resultSchema={actionDefinition.resultSchema} payloadSchema={actionDefinition.payloadSchema} walletAddress={walletAddress} isSimulation={isSimulation} executionStatus={childState?.status} /></div>;
    $[6] = actionDefinition;
    $[7] = childState?.result;
    $[8] = childState?.status;
    $[9] = isSimulation;
    $[10] = resolvedPayload;
    $[11] = walletAddress;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== childDef || $[14] !== childState?.context?.loopVars || $[15] !== executionState || $[16] !== expandedPayloads || $[17] !== iterStepKey || $[18] !== setExpandedPayloads) {
    t12 = !!childDef?.payload && Object.keys(childDef.payload).length > 0 && (childDef.type === "code-transform" ? <div><label className="micro-label">Payload</label><div className="mt-1"><CodeTransformPayload payload={childDef.payload} expandAll={expandedPayloads.has(iterStepKey)} executionState={executionState} /></div></div> : <div><div className="flex items-center justify-between"><label className="micro-label">Payload</label>{!expandedPayloads.has(iterStepKey) && hasNestedContent(childDef.payload) && <button onClick={() => setExpandedPayloads(prev => new Set(prev).add(iterStepKey))} className="micro-label text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400">Expand all</button>}</div><div className="emboss-inner !bg-ds-neutral-100 dark:!bg-ds-neutral-800 mt-1 max-h-40 w-full overflow-y-auto rounded-lg p-4 font-mono text-xs"><EnhancedJsonViewWithCopy value={childDef.payload} defaultExpanded={true} expandAll={expandedPayloads.has(iterStepKey)} executionState={executionState} loopVars={childState?.context?.loopVars as Record<string, unknown> | undefined} showExpandAll={false} /></div></div>);
    $[13] = childDef;
    $[14] = childState?.context?.loopVars;
    $[15] = executionState;
    $[16] = expandedPayloads;
    $[17] = iterStepKey;
    $[18] = setExpandedPayloads;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== childState) {
    t13 = !!childState?.context?.loopVars && <div><label className="micro-label">Loop Variables</label><div className="emboss-inner !bg-ds-neutral-100 dark:!bg-ds-neutral-800 mt-1 max-h-40 w-full overflow-y-auto rounded-lg p-4 font-mono text-xs"><EnhancedJsonViewWithCopy value={childState.context.loopVars} defaultExpanded={true} showExpandAll={false} /></div></div>;
    $[20] = childState;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== childDef?.payload || $[23] !== childDef?.type || $[24] !== childNodeId || $[25] !== childState || $[26] !== expandedPayloads || $[27] !== getNodeDisplayName || $[28] !== iterStepKey || $[29] !== setExpandedPayloads) {
    t14 = childState?.result && Object.keys(childState.result).length > 0 && <div>{!(childState.result.error && typeof childState.result.error === "string") && <div className="flex items-center justify-between"><label className="micro-label">Result</label>{!expandedPayloads.has(`${iterStepKey}-result`) && hasNestedContent(childState.result) && <button onClick={() => setExpandedPayloads(prev_0 => new Set(prev_0).add(`${iterStepKey}-result`))} className="micro-label text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400">Expand all</button>}</div>}<div className="mt-1"><ResultDisplay result={childState.result} expandAll={expandedPayloads.has(`${iterStepKey}-result`)} nodeState={{
          input: childDef?.payload,
          result: childState.result
        }} nodeInfo={{
          name: getNodeDisplayName(childDef?.type || childNodeId),
          type: childDef?.type
        }} /></div></div>;
    $[22] = childDef?.payload;
    $[23] = childDef?.type;
    $[24] = childNodeId;
    $[25] = childState;
    $[26] = expandedPayloads;
    $[27] = getNodeDisplayName;
    $[28] = iterStepKey;
    $[29] = setExpandedPayloads;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== childDef?.payload || $[32] !== childDef?.type || $[33] !== childNodeId || $[34] !== childState || $[35] !== getNodeDisplayName) {
    t15 = childState?.status === "failure" && childState?.error && <div><label className="micro-label">Error</label><div className="mt-1"><ErrorDisplay error={childState.error} nodeState={{
          input: childDef?.payload,
          result: childState.result
        }} nodeInfo={{
          name: getNodeDisplayName(childDef?.type || childNodeId),
          type: childDef?.type
        }} /></div></div>;
    $[31] = childDef?.payload;
    $[32] = childDef?.type;
    $[33] = childNodeId;
    $[34] = childState;
    $[35] = getNodeDisplayName;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== childConnector || $[38] !== childDuration || $[39] !== childLogoUrl || $[40] !== isExpanded || $[41] !== isFirst || $[42] !== isLast || $[43] !== onToggle || $[44] !== t10 || $[45] !== t11 || $[46] !== t12 || $[47] !== t13 || $[48] !== t14 || $[49] !== t15 || $[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t16 = <RunTimelineIterationRow displayName={t7} nodeType={t8} status={t9} duration={childDuration} error={t10} logoUrl={childLogoUrl} connector={childConnector} isFirst={isFirst} isLast={isLast} isExpanded={isExpanded} onToggle={onToggle}>{t11}{t12}{t13}{t14}{t15}</RunTimelineIterationRow>;
    $[37] = childConnector;
    $[38] = childDuration;
    $[39] = childLogoUrl;
    $[40] = isExpanded;
    $[41] = isFirst;
    $[42] = isLast;
    $[43] = onToggle;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  return t16;
}